/**
 * Branded primitive types used across Leviathan BI schemas.
 *
 * Branding prevents accidental mixing of semantically different UUID/string
 * fields (e.g. `UserId` and `TenantId`) at compile time while remaining
 * zero-cost at runtime.
 */
import { z } from 'zod';
export const TenantId = z.uuid().brand();
export const UserId = z.uuid().brand();
export const InvitationId = z.uuid().brand();
export const ConnectorId = z.uuid().brand();
export const JobId = z.uuid().brand();
export const JobRunId = z.uuid().brand();
export const UploadId = z.uuid().brand();
/** Cognito subject claim ("sub"). Not a UUID in all Cognito configurations. */
export const CognitoSub = z.string().min(1).max(128).brand();
/** RFC 5321 max email length = 254. Practical validation; accepts what Cognito accepts. */
export const Email = z.email().max(254).brand();
/**
 * Tenant slug. 3–31 chars, lowercase letter starter, letters and digits only.
 * The format matches MySQL identifier rules so the slug can be used verbatim
 * in the schema name (`lbi_<slug>` / `lbi_test_<slug>`). Immutable after
 * creation. Mirrors `tenants.slug VARCHAR(50) UNIQUE` in leviathan_master.
 */
export const TenantSlug = z
    .string()
    .regex(/^[a-z][a-z0-9]{2,30}$/, 'Invalid slug: must start with a lowercase letter and contain 3–31 lowercase letters or digits')
    .brand();
/**
 * ISO 8601 timestamp with offset (e.g. "2026-04-16T10:00:00Z").
 * Preferred backend format for `created_at`, `updated_at`, etc.
 */
export const ISO8601Date = z.iso.datetime({ offset: true }).brand();
/**
 * Calendar date with no time component (e.g. "2026-04-16").
 * Used for analytics `dateRange.from` / `dateRange.to` params.
 */
export const CalendarDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date: expected YYYY-MM-DD')
    .brand();
/**
 * Monetary value in the tenant's reporting currency.
 *
 * mysql2 returns `DECIMAL` columns as strings to preserve precision. `z.coerce.number`
 * transparently handles both the string and number cases. Callers that need exact
 * arithmetic should work with the original string form.
 */
export const Money = z.coerce.number().brand();
/**
 * Percentage as a ratio in [-1, 1] (e.g. -0.15 = -15%).
 * Variation fields in analytics KPIs use this convention.
 */
export const Percentage = z.coerce.number().min(-1).max(1).brand();
/** Generic non-negative integer used for counts, durations, pagination bounds. */
export const NonNegativeInt = z.coerce.number().int().nonnegative().brand();
/** Generic positive integer. */
export const PositiveInt = z.coerce.number().int().positive().brand();
//# sourceMappingURL=index.js.map